// SPDX-License-Identifier: GPL-3.0-or-later
//
// Typst compile engine — replaces SwiftLaTeX. Lazy-loaded singleton: first
// compile pays the WASM fetch + instantiate cost (~5-10 s for ~28 MB
// uncompressed engine), subsequent compiles are fast.
//
// We ship the WASM precompressed under `/typst/typst_ts_web_compiler_bg.wasm.gz`
// (~10 MB) because Cloudflare's per-asset 25 MB limit doesn't accept the raw
// 28 MB binary. The browser decompresses via `DecompressionStream` before
// instantiation.

import { $typst, TypstSnippet } from '@myriaddreamin/typst.ts/contrib/snippet';

const WASM_GZ_URL = '/typst/typst_ts_web_compiler_bg.wasm.gz';

let initPromise: Promise<void> | null = null;

async function fetchAndDecompress(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok || !res.body) {
    throw new Error(`Failed to fetch ${url}: HTTP ${res.status}`);
  }
  // The server may or may not auto-decode .gz. Vite dev sends
  // `Content-Encoding: gzip` so the browser already decompresses; Cloudflare
  // Pages serves .gz as-is. Detect by gzip magic (1F 8B) and decompress only
  // if still compressed — running DecompressionStream on raw wasm throws and
  // surfaces as `TypeError: Failed to fetch`.
  const raw = new Uint8Array(await res.arrayBuffer());
  if (raw.length >= 2 && raw[0] === 0x1f && raw[1] === 0x8b) {
    const stream = new Response(raw).body!.pipeThrough(new DecompressionStream('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  }
  return raw;
}

async function init(): Promise<void> {
  const wasmBytes = await fetchAndDecompress(WASM_GZ_URL);
  $typst.setCompilerInitOptions({
    getModule: () => wasmBytes,
  });
  // Self-host the body font (Libertinus Serif, the current name for the font
  // formerly called "Linux Libertine") from /public. Previously we fetched
  // Typst's default assets from jsdelivr at compile time — when that fetch
  // failed (offline, blocked, CDN hiccup) init rejected and EVERY compile
  // threw, leaving documents as ".typ only". Self-hosting makes compile
  // deterministic and offline-capable.
  //
  // Fetch the fonts ourselves and hand the bytes to preloadFonts as
  // Uint8Array. The SDK's own URL→fetch path was throwing "Failed to fetch"
  // with no Network entry and no caller in our fetch interceptor — bypassing
  // it removes a layer of opacity and we get a real error if the asset 404s.
  const fontNames = [
    'LibertinusSerif-Regular.otf',
    'LibertinusSerif-Bold.otf',
    'LibertinusSerif-Italic.otf',
    'LibertinusSerif-BoldItalic.otf',
  ];
  const fontBytes = await Promise.all(
    fontNames.map(async (name) => {
      const url = `/typst/fonts/${name}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch font ${url}: HTTP ${res.status}`);
      return new Uint8Array(await res.arrayBuffer());
    }),
  );
  $typst.use(
    TypstSnippet.disableDefaultFontAssets(),
    TypstSnippet.preloadFonts(fontBytes),
  );
}

function ensureInit(): Promise<void> {
  if (!initPromise) initPromise = init();
  return initPromise;
}

export interface CompileResult {
  pdf?: Uint8Array;
  status: number;
  log: string;
}

export async function compileTypstToPdf(
  source: string,
): Promise<CompileResult> {
  try {
    await ensureInit();
    const pdf = await $typst.pdf({ mainContent: source });
    if (!pdf) {
      return { status: 1, log: 'Typst returned an empty PDF (compile failed).' };
    }
    return { status: 0, log: '', pdf };
  } catch (err) {
    const msg = err instanceof Error ? (err.stack ?? err.message) : String(err);
    return { status: 1, log: msg };
  }
}
